import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/utils/api';
import { PartTitle, OP_STATUS, InitialRequest, DiagnosticPart, OperationsPart } from './WorkshopSheetParts';

const APPT_STATUS = { scheduled: 'Planifié', in_progress: 'En cours', completed: 'Terminé', cancelled: 'Annulé' };

const DoneRow = ({ woId, op, onChanged }) => {
  const [o, setO] = useState(op);
  useEffect(() => setO(op), [op]);
  const save = async (patch = {}) => {
    try { await axios.put(`${API}/work-orders/${woId}/operations/${op.id}`, { ...o, ...patch, time_spent_h: parseFloat(o.time_spent_h) || 0 }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="p-3 rounded-lg border bg-emerald-50/40 space-y-2" data-testid={`wo-done-${op.id}`}>
      <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="flex-1 text-sm font-semibold">{op.name}</span>
        <button type="button" className="text-xs underline text-slate-500" onClick={() => save({ status: 'in_progress' })} data-testid={`wo-done-reopen-${op.id}`}>Rouvrir</button></div>
      <div className="grid md:grid-cols-[1fr_110px] gap-2">
        <Input className="h-8 text-sm" value={o.parts_used} onChange={e => setO({ ...o, parts_used: e.target.value })} onBlur={() => save()} placeholder="Pièces utilisées" data-testid={`wo-done-parts-${op.id}`} />
        <Input className="h-8 text-sm" type="number" step="0.25" min="0" value={o.time_spent_h || ''} onChange={e => setO({ ...o, time_spent_h: e.target.value })} onBlur={() => save()} placeholder="Temps (h)" data-testid={`wo-done-time-${op.id}`} />
      </div>
      <Input className="h-8 text-sm" value={o.done_notes} onChange={e => setO({ ...o, done_notes: e.target.value })} onBlur={() => save()} placeholder="Observation" data-testid={`wo-done-notes-${op.id}`} />
    </div>
  );
};

const DonePart = ({ woId, workOrder, onChanged }) => {
  const done = (workOrder.operations || []).filter(o => o.status === 'done');
  const [notes, setNotes] = useState(workOrder.end_notes || '');
  const total = done.reduce((s, o) => s + (parseFloat(o.time_spent_h) || 0), 0);
  const saveNotes = async () => {
    try { await axios.put(`${API}/work-orders/${woId}/workshop-sheet`, { end_notes: notes }); toast.success('Observations enregistrées'); onChanged(); } catch { toast.error('Erreur'); }
  };
  return (
    <Card className="p-4 rounded-xl" data-testid="wo-part-done">
      <PartTitle n="3" title="Travaux réalisés" hint="Opérations terminées, pièces utilisées, temps passé et observations" />
      <div className="space-y-2">{done.map(op => <DoneRow key={op.id} woId={woId} op={op} onChanged={onChanged} />)}
        {done.length === 0 && <p className="text-sm text-slate-500">Aucune opération terminée pour l'instant.</p>}</div>
      {done.length > 0 && <p className="text-sm mt-2 font-semibold" data-testid="wo-done-total">Temps total : {total.toFixed(2)} h</p>}
      <p className="text-xs font-semibold text-slate-600 mt-4 mb-1">Observations de fin d'intervention</p>
      <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} data-testid="wo-end-notes-input" />
      {notes !== (workOrder.end_notes || '') && <Button size="sm" className="mt-2" onClick={saveNotes} data-testid="wo-end-notes-save-btn">Enregistrer</Button>}
    </Card>
  );
};

export const StatusStrip = ({ workOrder, appointment }) => {
  const ops = (workOrder.operations || []).filter(o => o.status !== 'cancelled');
  const done = ops.filter(o => o.status === 'done').length;
  const counts = Object.keys(OP_STATUS).map(k => [k, ops.filter(o => o.status === k).length]).filter(([k, n]) => n && k !== 'done');
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="wo-status-strip">
      <span className="px-3 py-1 rounded-full bg-slate-100 font-semibold" data-testid="wo-appt-status">Rendez-vous : {APPT_STATUS[appointment?.status] || '—'}</span>
      <span className="px-3 py-1 rounded-full bg-[#0E1116] text-[#FACC15] font-semibold" data-testid="wo-works-status">Travaux : {ops.length ? `${done}/${ops.length} terminés` : 'À définir'}</span>
      {counts.map(([k, n]) => <span key={k} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${OP_STATUS[k][1]}`}>{n} {OP_STATUS[k][0].toLowerCase()}</span>)}
    </div>
  );
};

export const WorkshopSheet = ({ woId, workOrder, appointment, onChanged, photos }) => (
  <div className="space-y-4" data-testid="wo-workshop-sheet">
    <StatusStrip workOrder={workOrder} appointment={appointment} />
    <InitialRequest workOrder={workOrder} appointment={appointment} />
    <DiagnosticPart woId={woId} workOrder={workOrder}>{photos}</DiagnosticPart>
    <OperationsPart woId={woId} workOrder={workOrder} onChanged={onChanged} />
    <DonePart woId={woId} workOrder={workOrder} onChanged={onChanged} />
  </div>
);

export const Fold = ({ title, children, testid, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 font-bold text-sm" data-testid={`${testid}-toggle`}>
        {title}<ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-4">{children}</div>}
    </div>
  );
};
